import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Form, Alert, Badge, Tab, Tabs } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Group } from '../types';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { authApi, knowledgeBaseApi } from '../services/api';

const Profile: React.FC = () => {
  const { user: currentUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [kbAssistantVisibleForGeneral, setKbAssistantVisibleForGeneral] = useState<boolean>(false);
  
  // Estados para troca de senha
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [savingEmailPrefs, setSavingEmailPrefs] = useState(false);
  const [savingKbAssistantVisibility, setSavingKbAssistantVisibility] = useState(false);

  useEffect(() => {
    // Verificar se veio do login com flag de troca obrigatória
    const state = location.state as { mustChangePassword?: boolean } | null;
    if (state?.mustChangePassword || user?.must_change_password) {
      setMustChangePassword(true);
    }
    fetchProfile();
    fetchGroups();
    if (currentUser?.role === 'admin') {
      fetchKbAssistantVisibility();
    }
  }, [location, user?.must_change_password, currentUser?.role]);

  const fetchProfile = async () => {
    try {
      const data = await authApi.getMe();
      setUser(data);
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
      setError('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me/groups`);
      setGroups(response.data);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const fetchKbAssistantVisibility = async () => {
    try {
      const data = await knowledgeBaseApi.getAssistantVisibility();
      setKbAssistantVisibleForGeneral(Boolean(data.assistant_visible_for_general));
    } catch (error) {
      console.error('Erro ao carregar configuração do assistente da base:', error);
      setError('Não foi possível carregar a configuração do assistente da base.');
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validações
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Todos os campos são obrigatórios');
      return;
    }

    if (newPassword.length < 6) {
      setError('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (currentPassword === newPassword) {
      setError('A nova senha deve ser diferente da senha atual');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API_URL}/api/auth/me/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSuccess('Senha alterada com sucesso!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMustChangePassword(false);
      // Atualizar perfil para remover flag
      await fetchProfile();
      // Se estava obrigado a trocar, redirecionar para dashboard após alguns segundos
      if (location.state && (location.state as { mustChangePassword?: boolean }).mustChangePassword) {
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleEmailPreferenceToggle = async (
    field: 'email_notify_ticket_opened' | 'email_notify_ticket_comment' | 'email_notify_ticket_closed',
    value: boolean
  ) => {
    setError(null);
    setSuccess(null);
    if (!user) return;

    const optimisticUser = { ...user, [field]: value };
    setUser(optimisticUser);
    setSavingEmailPrefs(true);

    try {
      const updated = await authApi.updateEmailNotificationPreferences({ [field]: value });
      setUser(updated);
      setSuccess('Preferências de e-mail atualizadas com sucesso.');
    } catch (err: any) {
      setUser(user);
      setError(err?.response?.data?.detail || 'Erro ao atualizar preferências de notificação por e-mail.');
    } finally {
      setSavingEmailPrefs(false);
    }
  };

  const handleKbAssistantVisibilityToggle = async (checked: boolean) => {
    setError(null);
    setSuccess(null);
    const previous = kbAssistantVisibleForGeneral;
    setKbAssistantVisibleForGeneral(checked);
    setSavingKbAssistantVisibility(true);
    try {
      const updated = await knowledgeBaseApi.updateAssistantVisibilityForGeneral(checked);
      setKbAssistantVisibleForGeneral(Boolean(updated.assistant_visible_for_general));
      setSuccess('Configuração do assistente da base atualizada.');
    } catch (error: any) {
      setKbAssistantVisibleForGeneral(previous);
      setError(error?.response?.data?.detail || 'Não foi possível atualizar a configuração do assistente da base.');
    } finally {
      setSavingKbAssistantVisibility(false);
    }
  };

  if (loading) {
    return <div className="loading-spinner">Carregando...</div>;
  }

  if (!user) {
    return <div>Erro ao carregar perfil</div>;
  }

  return (
    <div>
      <h2 className="mb-4">Meu Perfil</h2>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {mustChangePassword && (
        <Alert variant="warning" className="mb-4">
          <strong>⚠️ Atenção:</strong> Você precisa trocar sua senha antes de continuar usando o sistema.
          Por favor, acesse a aba "Trocar Senha" para alterá-la.
        </Alert>
      )}

      <Tabs defaultActiveKey={mustChangePassword ? "password" : "profile"} className="mb-4">
        <Tab eventKey="profile" title="Perfil">
          <Card className="mt-3">
            <Card.Header>
              <h5>Informações do Usuário</h5>
            </Card.Header>
            <Card.Body>
              <Table borderless>
                <tbody>
                  <tr>
                    <td style={{ width: '200px', fontWeight: 'bold' }}>ID:</td>
                    <td>{user.id}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Nome:</td>
                    <td>{user.name}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Email:</td>
                    <td>{user.email}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Função:</td>
                    <td>
                      <Badge bg={user.role === 'admin' ? 'danger' : user.role === 'analista' ? 'info' : 'secondary'}>
                        {user.role}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Status:</td>
                    <td>
                      <Badge bg={user.is_active ? 'success' : 'secondary'}>
                        {user.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Data de Criação:</td>
                    <td>{new Date(user.created_at).toLocaleString('pt-BR')}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="groups" title="Grupos">
          <Card className="mt-3">
            <Card.Header>
              <h5>Grupos Associados</h5>
            </Card.Header>
            <Card.Body>
              {groups.length === 0 ? (
                <p className="text-muted">Você não está associado a nenhum grupo.</p>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nome</th>
                      <th>Descrição</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((group) => (
                      <tr key={group.id}>
                        <td>{group.id}</td>
                        <td>{group.name}</td>
                        <td>{group.description || '-'}</td>
                        <td>
                          <Badge bg={group.is_active ? 'success' : 'secondary'}>
                            {group.is_active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="password" title="Trocar Senha">
          <Card className="mt-3">
            <Card.Header>
              <h5>Alterar Senha</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleChangePassword}>
                <Form.Group className="mb-3">
                  <Form.Label>Senha Atual</Form.Label>
                  <Form.Control
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Digite sua senha atual"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Nova Senha</Form.Label>
                  <Form.Control
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Digite a nova senha (mínimo 6 caracteres)"
                    required
                    minLength={6}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Confirmar Nova Senha</Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirme a nova senha"
                    required
                    minLength={6}
                  />
                </Form.Group>

                <Button variant="primary" type="submit" disabled={changingPassword}>
                  {changingPassword ? 'Alterando...' : 'Alterar Senha'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="notifications" title="Notificações por E-mail">
          <Card className="mt-3">
            <Card.Header>
              <h5>Preferências de Notificação</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Defina quais eventos de chamados devem enviar e-mail para você.
              </p>
              <Form>
                <Form.Check
                  id="profile-email-notify-ticket-opened"
                  type="switch"
                  className="mb-3"
                  label="Abertura de chamado"
                  checked={user.email_notify_ticket_opened !== false}
                  disabled={savingEmailPrefs}
                  onChange={(e) => handleEmailPreferenceToggle('email_notify_ticket_opened', e.target.checked)}
                />
                <Form.Check
                  id="profile-email-notify-ticket-comment"
                  type="switch"
                  className="mb-3"
                  label="Novo comentário público"
                  checked={user.email_notify_ticket_comment !== false}
                  disabled={savingEmailPrefs}
                  onChange={(e) => handleEmailPreferenceToggle('email_notify_ticket_comment', e.target.checked)}
                />
                <Form.Check
                  id="profile-email-notify-ticket-closed"
                  type="switch"
                  className="mb-1"
                  label="Encerramento de chamado"
                  checked={user.email_notify_ticket_closed !== false}
                  disabled={savingEmailPrefs}
                  onChange={(e) => handleEmailPreferenceToggle('email_notify_ticket_closed', e.target.checked)}
                />
              </Form>
            </Card.Body>
          </Card>
        </Tab>
        {user.role === 'admin' && (
          <Tab eventKey="admin-settings" title="Configurações (admin)">
            <Card className="mt-3">
              <Card.Header>
                <h5>Recursos administrativos</h5>
              </Card.Header>
              <Card.Body>
                <Form.Check
                  id="profile-kb-assistant-visibility"
                  type="switch"
                  className="mb-2"
                  label="Permitir assistente da base para o público geral"
                  checked={kbAssistantVisibleForGeneral}
                  disabled={savingKbAssistantVisibility}
                  onChange={(e) => handleKbAssistantVisibilityToggle(e.target.checked)}
                />
                <small className="text-muted">
                  Quando desativado, apenas administradores podem usar o assistente. Quando ativado, todos os usuários
                  podem visualizar e usar o assistente na base de conhecimento.
                </small>
              </Card.Body>
            </Card>
          </Tab>
        )}
      </Tabs>
    </div>
  );
};

export default Profile;

